export const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3.5 py-2.5 text-base font-semibold rounded-md transition-all whitespace-nowrap ${
      active
        ? "bg-white text-stone-900 shadow"
        : "text-stone-700 hover:text-stone-900 hover:bg-white/50"
    }`}
  >
    {children}
  </button>
);

export const ProseBlock = ({ paragraphs, fallback }) => {
  // Replace bare URLs in a string with clickable anchors so the
  // "case studies" / timeanddate.com links in the assumption
  // narrative are actually clickable.
  const linkify = (text) => {
    const parts = String(text).split(
      /(\bhttps?:\/\/[^\s<>]+|\bwww\.[^\s<>]+)/g
    );
    return parts.map((part, idx) => {
      if (/^(https?:\/\/|www\.)/.test(part)) {
        const href = part.startsWith("http") ? part : `https://${part}`;
        return (
          <a
            key={idx}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1B4F8A] underline hover:text-[#143E72] break-words"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Render each paragraph with light styling cues derived from the
  // Cognito Word-template conventions:
  //   * Lines starting with "=> " → section heading (bold, slightly
  //     larger, with a brand-blue accent dot in place of the "=>").
  //   * Lines starting with "    " (4-space indent) OR a leading "•"
  //     → bullet item with a real list bullet.
  //   * Lines starting with "- " → dash-prefixed list item.
  //   * URLs anywhere → rendered as clickable links.
  const renderParagraph = (p, i) => {
    const text = String(p || "");
    if (/^\s*=>\s+/.test(text)) {
      const heading = text.replace(/^\s*=>\s+/, "").trim();
      return (
        <h4
          key={i}
          className="mt-8 mb-3 text-stone-900 font-semibold text-base tracking-tight flex items-baseline gap-2"
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full bg-[#1B4F8A] shrink-0 translate-y-[-2px]"
            aria-hidden
          />
          {heading}
        </h4>
      );
    }
    const isIndented = /^ {2,}/.test(text);
    const isBulleted = /^[•·]\s/.test(text.trim());
    const isDashItem = /^-\s/.test(text.trim());
    if (isIndented || isBulleted) {
      const stripped = text.replace(/^[ \t]+/, "").replace(/^[•·]\s*/, "");
      return (
        <div
          key={i}
          className="flex items-baseline gap-2 mb-1.5 last:mb-0 ml-2"
        >
          <span className="text-[#1B4F8A] shrink-0">•</span>
          <span className="flex-1">{linkify(stripped)}</span>
        </div>
      );
    }
    if (isDashItem) {
      const stripped = text.replace(/^-\s+/, "");
      return (
        <div
          key={i}
          className="flex items-baseline gap-2 mb-1.5 last:mb-0 ml-2"
        >
          <span className="text-stone-400 shrink-0">·</span>
          <span className="flex-1">{linkify(stripped)}</span>
        </div>
      );
    }
    return (
      <p
        key={i}
        className="mb-4 last:mb-0 whitespace-pre-line"
      >
        {linkify(text)}
      </p>
    );
  };

  if (paragraphs?.length > 0) {
    return (
      <div className="mt-6 max-w-none text-stone-700 leading-relaxed text-[15px]">
        {paragraphs.map(renderParagraph)}
      </div>
    );
  }
  if (!fallback?.trim()) return null;
  const blocks = String(fallback).split(/\n{2,}/);
  return (
    <div className="mt-6 prose prose-stone max-w-none text-stone-700 leading-relaxed text-[15px]">
      {blocks.map((b, i) => (
        <p key={i} className="mb-4 last:mb-0 whitespace-pre-line">{b}</p>
      ))}
    </div>
  );
};

export const SectionHead = ({ n, title }) => (
  <div className="flex items-baseline gap-3 sm:gap-6">
    <span className="font-mono text-xs tracking-[0.2em] text-[#3EA8DC]">{n}</span>
    <h2 className="text-2xl md:text-3xl font-serif font-light text-stone-900">{title}</h2>
    <div className="flex-1 h-px bg-stone-200" />
  </div>
);
